import dedent from 'dedent';
import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'list-file' as const;

const argumentZod = z.object({
  path: z
    .string()
    .optional()
    .describe(
      'The virtual directory path to list. Examples: "site", "site/components". Defaults to root if omitted.'
    ),
});

export const listFileTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'List files',
  prettyStatus: {
    executing: 'Listing files...',
    executed: 'Listed files',
    failed: 'Failed to list files',
  },
  prettyErrors: {
    fallback: 'Unable to list files.',
    patterns: [
      {
        match: 'not a valid directory',
        message: 'Directory not found.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Lists files in the site's virtual file system. Use this to discover available components and builtin element types before reading them with read-file.

      The virtual file system contains only components, builtin elements, and site styles — there is no page file. Use \`read-page\` to read a page.

      Virtual directory structure:
        site/
          components/              — Site components (flat) + library directories
          components/{library}/    — Components from a specific installed library
          builtin/                 — All supported builtin element types (Section, DivBlock, Paragraph, etc.)

      Each file entry includes:
      - path: virtual file path (use with read-file to read the component)
      - name: human-readable name
      - id: component ID (use as component-id in <wf-component> for INSERT)
      - type: "component" (site), "library-component" (installed from library), "directory" (library folder), or "builtin-element" (native Webflow element type)

      Library components and code components are read-only — their internal structure can only be edited at the source site or in the source code. You can insert instances and set props, but cannot modify their internals.

      Call with no path or path "site" to see top-level directories.
      Call with path "site/components" to list site components and library directories.
      Call with path "site/components/{library-slug}" to list components from a specific library.
      Call with path "site/builtin" to list all builtin element types.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => true,
  isReadOnly: () => true,
  timeout: 15000,
});
